import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import Grid from '../common/Grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { apiEndpoints } from '../../config/api';

interface Element {
  id: string;
  symbol: string;
  name: string;
  atomicWeight: number;
}

interface SaltElement {
  elementId: string;
  symbol: string;
  percentage: number;
}

interface CustomSaltCreationProps {
  onSaltCreated: () => void;
}

const CustomSaltCreation: React.FC<CustomSaltCreationProps> = ({ onSaltCreated }) => {
  const [saltName, setSaltName] = useState('');
  const [saltFormula, setSaltFormula] = useState('');
  const [density, setDensity] = useState('');
  const [solubility, setSolubility] = useState('');
  const [elements, setElements] = useState<SaltElement[]>([]);
  const [availableElements, setAvailableElements] = useState<Element[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchElements();
  }, []);

  const fetchElements = async () => {
    try {
      const response = await fetch(apiEndpoints.elements.list);
      if (!response.ok) {
        throw new Error('Failed to fetch elements');
      }
      const data = await response.json();
      setAvailableElements(data);
    } catch (err) {
      setError('Failed to load elements');
    }
  };

  const addElement = () => {
    setElements([...elements, { elementId: '', symbol: '', percentage: 0 }]);
  };

  const removeElement = (index: number) => {
    setElements(elements.filter((_, i) => i !== index));
  };

  const updateElement = (index: number, field: keyof SaltElement, value: string | number) => {
    const updated = [...elements];
    if (field === 'elementId') {
      const element = availableElements.find(el => el.id === value);
      updated[index] = {
        ...updated[index],
        elementId: value as string,
        symbol: element?.symbol || ''
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setElements(updated);
  };

  const validateFormula = (formula: string): boolean => {
    // Basic chemical formula validation
    const formulaRegex = /^[A-Z][a-z]?(\d*\.?\d*[A-Z][a-z]?\d*)*(\([A-Z][a-z]?\d*\))*(\d*\.?\d*H2O)?$/;
    return formulaRegex.test(formula.replace(/\s/g, ''));
  };

  const validateSalt = (): string[] => {
    const errors: string[] = [];
    
    if (!saltName.trim()) {
      errors.push('Salt name is required');
    }
    
    if (!saltFormula.trim()) {
      errors.push('Chemical formula is required');
    } else if (!validateFormula(saltFormula)) {
      errors.push('Invalid chemical formula format');
    }
    
    if (elements.length === 0) {
      errors.push('At least one element must be specified');
    }
    
    const totalPercentage = elements.reduce((sum, el) => sum + el.percentage, 0);
    if (totalPercentage > 100) {
      errors.push('Total element percentages cannot exceed 100%');
    }
    
    const hasInvalidElements = elements.some(el => !el.elementId || el.percentage <= 0);
    if (hasInvalidElements) {
      errors.push('All elements must be selected with valid percentages');
    }
    
    const duplicateElements = elements.some((el, index) => 
      elements.findIndex(e => e.elementId === el.elementId) !== index
    );
    if (duplicateElements) {
      errors.push('Duplicate elements are not allowed');
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    
    const validationErrors = validateSalt();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }
    
    setLoading(true);
    
    try {
      const saltData = {
        name: saltName.trim(),
        formula: saltFormula.trim(),
        isCustom: true,
        density: density ? parseFloat(density) : null,
        solubility: solubility ? parseFloat(solubility) : null,
        elements: elements.map(el => ({
          elementId: el.elementId,
          percentage: el.percentage
        }))
      };
      
      const response = await fetch(apiEndpoints.salts.create, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saltData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create salt');
      }
      
      setSuccess(`Custom salt "${saltName}" created successfully!`);
      clearForm();
      onSaltCreated();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create salt');
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setSaltName('');
    setSaltFormula('');
    setDensity('');
    setSolubility('');
    setElements([]);
    setError(null);
    setSuccess(null);
  };

  const totalPercentage = elements.reduce((sum, el) => sum + el.percentage, 0);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Create Custom Salt
          </Typography>
          <ScienceIcon color="primary" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid xs={12} sm={6}>
            <TextField
              fullWidth
              label="Salt Name"
              value={saltName}
              onChange={(e) => setSaltName(e.target.value)}
              placeholder="e.g., Custom Calcium Nitrate"
              required
            />
          </Grid>

          <Grid xs={12} sm={6}>
            <TextField
              fullWidth
              label="Chemical Formula"
              value={saltFormula}
              onChange={(e) => setSaltFormula(e.target.value)}
              placeholder="e.g., Ca(NO3)2·4H2O"
              required
              helperText="Use standard chemical notation"
            />
          </Grid>

          <Grid xs={12} sm={6}>
            <TextField
              fullWidth
              label="Density (g/cm³)"
              type="number"
              value={density}
              onChange={(e) => setDensity(e.target.value)}
              placeholder="e.g., 2.5"
              inputProps={{ step: 0.1, min: 0 }}
            />
          </Grid>

          <Grid xs={12} sm={6}>
            <TextField
              fullWidth
              label="Solubility (g/L at 20°C)"
              type="number"
              value={solubility}
              onChange={(e) => setSolubility(e.target.value)}
              placeholder="e.g., 1290"
              inputProps={{ step: 1, min: 0 }}
            />
          </Grid>

          {/* Element Composition */}
          <Grid xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
              <Typography variant="subtitle1">
                Element Composition
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addElement}
                variant="outlined"
                size="small"
              >
                Add Element
              </Button>
            </Box>
            
            {totalPercentage > 0 && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={`Total: ${totalPercentage.toFixed(1)}%`}
                  color={totalPercentage > 100 ? 'error' : totalPercentage === 100 ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Box>
            )}
          </Grid>

          <Grid xs={12}>
            {elements.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Element</TableCell>
                      <TableCell>Percentage (%)</TableCell>
                      <TableCell width={60}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {elements.map((element, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <InputLabel>Element</InputLabel>
                            <Select
                              value={element.elementId}
                              label="Element"
                              onChange={(e) => updateElement(index, 'elementId', e.target.value)}
                            >
                              {availableElements.map((el) => (
                                <MenuItem key={el.id} value={el.id}>
                                  {el.symbol} - {el.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={element.percentage}
                            onChange={(e) => updateElement(index, 'percentage', parseFloat(e.target.value) || 0)}
                            inputProps={{ step: 0.1, min: 0, max: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => removeElement(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                Click "Add Element" to start defining the salt composition.
              </Alert>
            )}
          </Grid>

          {/* Actions */}
          <Grid xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={clearForm}
                startIcon={<ClearIcon />}
                disabled={loading}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                startIcon={<SaveIcon />}
                disabled={loading || elements.length === 0}
              >
                {loading ? 'Creating...' : 'Create Salt'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CustomSaltCreation;
